/**
 * SLA Configuration Index
 * Feature: 024-sla-tier-configuration
 *
 * Main settings page component with tabs for all SLA configuration features.
 * Spec 226 T035: on the canonical shell (PageLayout + kit Tabs + Card), replacing the
 * hand-built header, tab bar and gray card.
 */

import React, { useState } from 'react';
import { Layers } from 'lucide-react';
import { PageLayout, Tabs, Card } from '../../../components/ui';
import { SLATiersTab } from './SLATiersTab';
import ClientAssignmentsTab from './ClientAssignmentsTab';
import ClientOverridesTab from './ClientOverridesTab';
import MaintenanceWindowsTab from './MaintenanceWindowsTab';

type TabKey = 'tiers' | 'assignments' | 'overrides' | 'maintenance';

const TABS: Array<{ key: TabKey; label: string; component: React.ComponentType }> = [
  { key: 'tiers', label: 'SLA Tiers', component: SLATiersTab },
  { key: 'assignments', label: 'Client Assignments', component: ClientAssignmentsTab },
  { key: 'overrides', label: 'Client Overrides', component: ClientOverridesTab },
  { key: 'maintenance', label: 'Maintenance Windows', component: MaintenanceWindowsTab },
];

export const SLAConfiguration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('tiers');
  const ActiveComponent = TABS.find((t) => t.key === activeTab)?.component ?? (() => null);

  return (
    <PageLayout
      title="SLA Configuration"
      description="SLA tiers, client assignments, per-client overrides, and maintenance windows"
      icon={<Layers className="h-6 w-6" />}
      helpId="auto.sla-configuration"
    >
      <Tabs
        tabs={TABS.map(({ key, label }) => ({ key, label }))}
        value={activeTab}
        onChange={(key) => {
          const tab = TABS.find((t) => t.key === key);
          if (tab) setActiveTab(tab.key);
        }}
        className="mb-4"
      />
      <Card padding="lg">
        <ActiveComponent />
      </Card>
    </PageLayout>
  );
};

export default SLAConfiguration;
